const chapter = {
  id: 'digestion-absorption',
  slug: 'digestion-absorption',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Digestion and Absorption',
        hindi: 'पाचन और अवशोषण',
  },
  estReadMinutes: 50,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2-3 questions in NEET',
        hindi: 'NEET में 2-3 प्रश्न',
  },
  syllabusTags: ['Human Physiology', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'alimentary-canal',
      title: {
        english: 'Alimentary Canal',
        hindi: 'आहार नाल',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Mouth → Pharynx → Oesophagus → Stomach → Small intestine (duodenum, jejunum, ileum) → Large intestine (caecum, colon, rectum) → Anus. Total length ~9 m.',
        hindi: 'मुंह → ग्रसनी → ग्रासनली → पेट → छोटी आंत (ग्रहणी, जेजुनम, इलियम) → बड़ी आंत (सीकम, कोलन, मलाशय) → गुदा। कुल लंबाई ~9 मी.',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Teeth** — thecodont (in sockets), diphyodont (two sets), heterodont (4 types). Adult dental formula: **2 1 2 3 / 2 1 2 3 = 32**. Enamel = hardest substance in body.',
        hindi: '**दांत** - थेकोडॉन्ट (सॉकेट में), डिप्योडोंट (दो सेट), हेटेरोडॉन्ट (4 प्रकार)। वयस्क दंत फार्मूला: **2 1 2 3 / 2 1 2 3 = 32**। Enamel = hardest substance in body.',
            },
            {
              english: '**Tongue** — taste buds (sweet at tip, salty/sour at sides, bitter at back).',
        hindi: '**जीभ** - स्वाद कलिकाएँ (शीर्ष पर मीठा, किनारे पर salty/sour, पीछे से कड़वा)।',
            },
            {
              english: '**Salivary glands** — 3 pairs: parotid, submaxillary (submandibular), sublingual. Secrete saliva (1.0-1.5 L/day) containing **salivary amylase (ptyalin)** — starch → maltose at pH 6.8; lysozyme (antibacterial).',
        hindi: '**लार ग्रंथियाँ** - 3 जोड़े: पैरोटिड, सबमैक्सिलरी (सबमांडिबुलर), सबलिंगुअल। स्रावित लार (1.0-1.5 L/day) जिसमें **लार एमाइलेज (पीटालिन)** - पीएच 6.8 पर स्टार्च → माल्टोज़ होता है; लाइसोजाइम (जीवाणुरोधी)।',
            },
            {
              english: '**Stomach** — J-shaped; 3 parts: cardiac, fundic, pyloric. Capacity ~1.5 L. Mucosa has **gastric glands**:',
        hindi: '**पेट** — जे-आकार; 3 भाग: कार्डियक, फंडिक, पाइलोरिक। क्षमता ~1.5 एल. म्यूकोसा में **गैस्ट्रिक ग्रंथियां** होती हैं:',
            },
            {
              english: '• **Mucous (goblet) cells** — mucus.',
        hindi: '• **श्लेष्म (गॉब्लेट) कोशिकाएँ** — बलगम।',
            },
            {
              english: '• **Peptic / chief / zymogen cells** — pepsinogen, prorennin.',
        hindi: '• **पेप्टिक / चीफ / ज़ाइमोजेन कोशिकाएं** - पेप्सिनोजन, प्रोरेनिन।',
            },
            {
              english: '• **Parietal / oxyntic cells** — HCl & intrinsic factor (vit B₁₂ absorption).',
        hindi: '• **पार्श्विका / ऑक्सीनटिक कोशिकाएं** - एचसीएल और आंतरिक कारक (विट बी₁₂ अवशोषण)।',
            },
            {
              english: '**Small intestine** — longest (~6.5 m); duodenum (C-shaped, receives bile + pancreatic juice), jejunum, ileum. Inner surface has **villi** (with **microvilli — brush border**) → ↑ surface area ~600×.',
        hindi: '**छोटी आंत** — सबसे लंबी (~6.5 मीटर); ग्रहणी (सी-आकार, पित्त + अग्नाशयी रस प्राप्त करता है), जेजुनम, इलियम। आंतरिक सतह में **विली** है (**माइक्रोविली - ब्रश बॉर्डर** के साथ) → ↑ सतह क्षेत्र ~600×।',
            },
            {
              english: '**Large intestine** — caecum (vermiform appendix is vestigial); colon (ascending, transverse, descending, sigmoid); rectum; anus (sphincters). Function: water & electrolyte absorption.',
        hindi: '**बड़ी आंत** - अंधनाल (वर्मीफॉर्म अपेंडिक्स अवशेषी है); बृहदान्त्र (आरोही, अनुप्रस्थ, अवरोही, सिग्मॉइड); मलाशय; गुदा (स्फिंक्टर्स)। कार्य: जल एवं इलेक्ट्रोलाइट अवशोषण।',
            },
          ],
        },
      ],
    },
    {
      id: 'glands',
      title: {
        english: 'Digestive Glands',
        hindi: 'पाचक ग्रन्थियाँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Liver** — largest gland (~1.5 kg); located below diaphragm. Functional unit = **hepatic lobule** (hepatocytes). Secretes **bile** (~800 mL/day) into gall bladder via hepatic duct → bile duct → duodenum.',
        hindi: '**लिवर** — सबसे बड़ी ग्रंथि (~1.5 किग्रा); डायाफ्राम के नीचे स्थित है। कार्यात्मक unit = **hepatic lobule** (hepatocytes)। **पित्त** (~800 mL/day) को यकृत वाहिनी → पित्त वाहिनी → ग्रहणी के माध्यम से पित्ताशय में स्रावित करता है।',
            },
            {
              english: '• Bile = bile salts (sodium glycocholate, taurocholate), bile pigments (**bilirubin = yellow, biliverdin = green**), cholesterol, phospholipids. **No enzymes**. Functions: **emulsification of fats** (breaks into micelles), activates lipase, makes chyme alkaline.',
        hindi: '• Bile = bile salts (sodium glycocholate, टॉरोकोलेट), पित्त वर्णक (**bilirubin = yellow, biliverdin = green**), कोलेस्ट्रॉल, फॉस्फोलिपिड। **कोई एंजाइम नहीं**। कार्य: **वसा का पायसीकरण** (मिसेल में टूट जाता है), लाइपेस को सक्रिय करता है, काइम को क्षारीय बनाता है।',
            },
            {
              english: '**Pancreas** — heterocrine. Exocrine portion secretes pancreatic juice (~1.2 L/day) containing trypsinogen, chymotrypsinogen, procarboxypeptidase, amylase, lipase, nucleases. Enterokinase (from intestinal mucosa) activates trypsinogen → trypsin → activates other proenzymes.',
        hindi: '**अग्न्याशय** - हेटरोक्राइन। बहिःस्रावी भाग अग्नाशयी रस (~1.2 L/day) को स्रावित करता है जिसमें ट्रिप्सिनोजेन, काइमोट्रिप्सिनोजेन, प्रोकार्बोक्सीपेप्टिडेज़, एमाइलेज, लाइपेज, न्यूक्लीज होते हैं। एंटरोकिनेस (आंतों के म्यूकोसा से) ट्रिप्सिनोजेन को सक्रिय करता है → ट्रिप्सिन → अन्य प्रोएंजाइम को सक्रिय करता है।',
            },
          ],
        },
        {
          type: 'table',
          headers: ['Region', 'Enzyme/Secretion', 'Substrate', 'Product'],
          rows: [
            ['Mouth', 'Salivary amylase', 'Starch', 'Maltose'],
            ['Stomach', 'Pepsin (HCl)', 'Proteins', 'Peptones, proteoses'],
            ['Stomach (infants)', 'Rennin', 'Casein (milk)', 'Calcium paracaseinate'],
            ['Pancreas', 'Trypsin', 'Proteins/peptones', 'Peptides + amino acids'],
            ['Pancreas', 'Pancreatic amylase', 'Starch', 'Disaccharides'],
            ['Pancreas', 'Pancreatic lipase', 'Fats', 'Diglycerides → monoglycerides + FA'],
            ['Intestine', 'Maltase, sucrase, lactase', 'Disaccharides', 'Monosaccharides'],
            ['Intestine', 'Aminopeptidase, dipeptidase', 'Peptides', 'Amino acids'],
            ['Intestine', 'Lipase', 'Fats', 'Fatty acids + glycerol'],
            ['Intestine', 'Nucleotidase, nucleosidase', 'Nucleic acids', 'Bases + sugars'],
          ],
        },
      ],
    },
    {
      id: 'absorption',
      title: {
        english: 'Absorption & Assimilation',
        hindi: 'अवशोषण और स्वांगीकरण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Mostly in **small intestine** (jejunum + ileum). Mouth: drugs (nicotine). Stomach: water, alcohol, simple sugars.',
        hindi: 'अधिकतर **छोटी आंत** (जेजुनम ​​+ इलियम) में। मुंह: दवाएं (निकोटीन)। पेट: पानी, शराब, साधारण शर्करा।',
            },
            {
              english: 'Mechanisms: passive (simple diffusion, facilitated), active (against gradient, ATP), endocytosis (fats).',
        hindi: 'तंत्र: निष्क्रिय (सरल प्रसार, सुगम), सक्रिय (ग्रेडिएंट, एटीपी के विरुद्ध), एंडोसाइटोसिस (वसा)।',
            },
            {
              english: '**Glucose, amino acids, electrolytes** → blood capillaries → hepatic portal vein → liver.',
        hindi: '**ग्लूकोज, अमीनो एसिड, इलेक्ट्रोलाइट्स** → रक्त केशिकाएं → यकृत पोर्टल शिरा → यकृत।',
            },
            {
              english: '**Fatty acids + glycerol** → micelles → enter mucosa → re-formed to triglycerides → coated with protein = **chylomicrons** → lymph (lacteals) → thoracic duct → blood.',
        hindi: '**फैटी एसिड + ग्लिसरॉल** → मिसेल → म्यूकोसा में प्रवेश करते हैं → ट्राइग्लिसराइड्स में फिर से बनते हैं → protein = **chylomicrons** → lymph (lacteals) → thoracic duct → blood से लेपित होते हैं।',
            },
            {
              english: 'Large intestine absorbs water, some minerals, vit K (from bacterial flora).',
        hindi: 'बड़ी आंत पानी, कुछ खनिज, विटामिन K (जीवाणु वनस्पतियों से) को अवशोषित करती है।',
            },
          ],
        },
      ],
    },
    {
      id: 'hormones',
      title: {
        english: 'Hormonal Control of Digestion',
        hindi: 'पाचन का हार्मोनल नियंत्रण',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Hormone', 'Source', 'Stimulus', 'Action'],
          rows: [
            ['Gastrin', 'Stomach (G cells)', 'Food in stomach', '↑ HCl & pepsinogen secretion'],
            ['Secretin', 'Duodenum', 'Acidic chyme', '↑ pancreatic bicarbonate; ↓ HCl'],
            ['Cholecystokinin (CCK)', 'Duodenum', 'Fats & proteins', '↑ pancreatic enzymes; gall bladder contraction'],
            ['GIP (gastric inhibitory peptide)', 'Duodenum', 'Glucose, fats', '↓ gastric motility; ↑ insulin'],
          ],
        },
      ],
    },
    {
      id: 'disorders',
      title: {
        english: 'Disorders & Calorific Values',
        hindi: 'विकार और कैलोरी मान',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Jaundice** — liver dysfunction; bilirubin in skin & eyes (yellowish).',
        hindi: '**पीलिया** — यकृत की शिथिलता; त्वचा और आंखों में बिलीरुबिन (पीलापन)।',
            },
            {
              english: '**Vomiting** — reverse peristalsis controlled by vomit centre in medulla.',
        hindi: '**उल्टी** - मज्जा में उल्टी केंद्र द्वारा नियंत्रित रिवर्स क्रमाकुंचन।',
            },
            {
              english: '**Diarrhoea** — abnormal frequent watery faecal discharge; loss of water & electrolytes (cholera, food poisoning).',
        hindi: '**दस्त** - असामान्य बार-बार पानी जैसा मल स्राव; पानी और इलेक्ट्रोलाइट्स की हानि (हैजा, भोजन विषाक्तता)।',
            },
            {
              english: '**Constipation** — faeces retained; irregular bowel.',
        hindi: '**कब्ज** - मल रुका रहता है; अनियमित आंत्र.',
            },
            {
              english: '**Indigestion / dyspepsia** — incomplete digestion; bloating, gas.',
        hindi: '**अपच / डिस्पेप्सिया** — अधूरा पाचन; पेट फूलना और गैस जैसी समस्याएँ।',
            },
            {
              english: '**PEM (Protein-Energy Malnutrition)**:',
        hindi: '**पीईएम (प्रोटीन-ऊर्जा कुपोषण)**:',
            },
            {
              english: '• **Marasmus** — < 1 yr; low protein **and** calories; thin emaciated, wrinkled skin.',
        hindi: '• **मैरास्मस** — <1 वर्ष; कम प्रोटीन **और** कैलोरी; पतली क्षीण, झुर्रियों वाली त्वचा।',
            },
            {
              english: '• **Kwashiorkor** — > 1 yr; adequate calories, deficient protein; oedema, pot-belly.',
        hindi: '• **क्वाशियोरकोर** — > 1 वर्ष; पर्याप्त कैलोरी, कमी प्रोटीन; एडिमा, पॉट-बेली।',
            },
            {
              english: '**Calorific values**: Carbs = 4.1 kcal/g, Proteins = 4.0 kcal/g (5.65 raw), Fats = 9.45 kcal/g.',
        hindi: '**कैलोरी मान**: कार्ब्स = 4.1 किलो कैलोरी/ग्राम, प्रोटीन = 4.0 किलो कैलोरी/ग्राम (5.65 कच्चा), वसा = 9.45 किलो कैलोरी/ग्राम।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\text{Dental formula (adult): 2123/2123 = 32}',
      desc: {
        english: '',
        hindi: 'दंत सूत्र (वयस्क): 2123/2123 = 32',
      },
    },
    {
      latex: '\\text{Bile: emulsifies fats; no enzymes}',
      desc: {
        english: '',
        hindi: 'पित्त: वसा का पायसीकरण करता है; कोई एंजाइम नहीं',
      },
    },
    {
      latex: '\\text{Trypsinogen} \\xrightarrow{\\text{enterokinase}} \\text{Trypsin}',
      desc: {
        english: '',
        hindi: 'ट्रिप्सिनोजेन ट्रिप्सिन',
      },
    },
    {
      latex: '\\text{Carbs} = 4.1\\ \\text{kcal/g, Fats} = 9.45\\ \\text{kcal/g, Protein} = 4.0\\ \\text{kcal/g}',
      desc: {
        english: '',
        hindi: 'कार्ब्स = 4.1\\ kcal/g, वसा = 9.45\\ kcal/g, प्रोटीन = 4.0\\ kcal/g',
      },
    },
    {
      latex: '\\text{Gastrin} \\uparrow \\text{HCl}; \\text{ Secretin} \\uparrow HCO_3^-; \\text{ CCK} \\uparrow \\text{enzymes}',
      desc: {
        english: '',
        hindi: 'गैस्ट्रिन एचसीएल; सीक्रेटिन एचसीओ 3 -; सीसीके एंजाइम',
      },
    },
    {
      latex: '\\text{Fats} \\to \\text{chylomicrons} \\to \\text{lacteals (lymph)}',
      desc: {
        english: '',
        hindi: 'वसा काइलोमाइक्रोन लैक्टियल्स (लिम्फ)',
      },
    },
    {
      latex: '\\text{Vit B}_{12} \\text{ absorption needs intrinsic factor (parietal cells)}',
      desc: {
        english: '',
        hindi: 'विटामिन बी 12 अवशोषण के लिए आंतरिक कारक (पार्श्विका कोशिकाएं) की आवश्यकता होती है',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Enzyme',
      problem: {
        english: 'Enterokinase activates:',
        hindi: 'एंटरोकिनेस सक्रिय होता है:',
      },
      answer: {
        english: 'Trypsinogen → Trypsin',
        hindi: 'ट्रिप्सिनोजेन → ट्रिप्सिन',
      },
      solution: {
        english: 'Secreted by intestinal mucosa.',
        hindi: 'आंतों के म्यूकोसा द्वारा स्रावित।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Hormone',
      problem: {
        english: 'Cholecystokinin causes:',
        hindi: 'कोलेसीस्टोकिनिन कारण:',
      },
      answer: {
        english: 'Contraction of gall bladder + release of pancreatic enzymes',
        hindi: 'पित्ताशय का संकुचन + अग्नाशयी एंजाइमों का निकलना',
      },
      solution: {
        english: 'Triggered by fats in duodenum.',
        hindi: 'यह ग्रहणी में वसा की उपस्थिति से उद्दीप्त होता है।',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'PEM',
      problem: {
        english: 'Marasmus is caused by deficiency of:',
        hindi: 'मरास्मस किसकी कमी के कारण होता है:',
      },
      answer: {
        english: 'Protein and calories',
        hindi: 'प्रोटीन और कैलोरी',
      },
      solution: {
        english: 'Below 1 year of age; severe wasting.',
        hindi: '1 वर्ष से कम आयु; गंभीर बर्बादी.',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Stomach',
      problem: {
        english: 'HCl in stomach is secreted by:',
        hindi: 'पेट में HCl स्रावित होता है:',
      },
      answer: {
        english: 'Parietal (oxyntic) cells',
        hindi: 'पार्श्विका (ऑक्सिनटिक) कोशिकाएं',
      },
      solution: {
        english: 'Also secrete intrinsic factor for B₁₂ absorption.',
        hindi: 'B₁₂ अवशोषण के लिए आंतरिक कारक भी स्रावित करते हैं।',
      },
    },
    {
      year: 2020,
      exam: 'NEET',
      tag: 'Absorption',
      problem: {
        english: 'Maximum absorption of food occurs in:',
        hindi: 'भोजन का अधिकतम अवशोषण होता है:',
      },
      answer: {
        english: 'Jejunum and ileum (small intestine)',
        hindi: 'जेजुनम और इलियम (छोटी आंत)',
      },
      solution: {
        english: 'Villi + microvilli increase surface area.',
        hindi: 'विली + माइक्रोविली सतह क्षेत्र को बढ़ाते हैं। **पारिस्थितिकी तंत्र** = प्रकृति की कार्यात्मक इकाई जहां जीवित जीव निर्जीव पर्यावरण के साथ बातचीत करते हैं।',
      },
    },
  ],
  next: {
    slug: 'breathing-exchange-gases',
    title: 'Breathing and Exchange of Gases',
  },
};

export default chapter;
